import { NextRouter } from "next/router"
import {
  AppleSignInParams,
  AuthComponent,
  EmbeddedRegisterApiParams,
  GoogleSignInParams,
  RegisterGateDialogInfo,
  RegisterParams,
  ServerErrorBody,
  SignInParams,
  WebHandoffParams,
} from "@/types/auth"
import { ProviderMismatchException } from "@/types/client-exceptions"
import { SimpleHttpError } from "@/types/http"
import { RegisterGateTypes } from "@/types/register-gate"
import { LoginAuthProvider } from "@/types/user"
import { Honeybadger } from "@honeybadger-io/react"

import { errorToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import {
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "@/lib/analytics/amplitude-utils"
import { setupAppleSignIn } from "@/lib/apple-sign-in"
import { appLandingPath } from "@/lib/auth-utils"
import { AuthV2Api } from "@/lib/auth-v2-api"
import { broadcastAuthEvent } from "@/lib/auth/broadcast-channel"
import { refreshAuthToken } from "@/lib/auth/client"
import {
  getEmailStepErrorMessage,
  getRegisterStepErrorMessage,
  getSignInStepErrorMessage,
} from "@/lib/auth/errors"
import { buildClientAxios } from "@/lib/client-axios"
import { EmbeddedAuthApi } from "@/lib/embedded-auth-api"
import { storeEmbeddedTokens } from "@/lib/embedded/token-storage"
import { EmailSchema } from "@/lib/form-utils"
import { setupGoogleSignIn } from "@/lib/google-sign-in"
import { isEmbedded } from "@/lib/is-embedded"
import { mtLocation } from "@/lib/mt-location"
import {
  clearPendingAuthAction,
  getPendingAuthAction,
  prepareTravelAssistantSetupAfterSignup,
} from "@/lib/pending-auth-action"
import { links } from "@/lib/routes"
import { selectUser } from "@/lib/storage/user-slice"
import { refreshCurrentUserInfo } from "@/lib/storage/user-thunks"
import { shouldTurnstile } from "@/lib/turnstile"
import { UserApi } from "@/lib/user-api"
import { present } from "@/lib/utils/presence"
import { removeParamFromUrl } from "@/lib/utils/url-params"
import { AmplitudeEmptyValue } from "../analytics/amplitude-types"
import { isSafeRelativeCallbackUrl } from "../auth-utils"
import { validateRegistrationFormValue } from "../registration-form-utils"
import { selectOrgHasNewsletter } from "./app-slice"
import {
  _resetAuthDialogProps,
  _setAuthDialogProps,
  initialAuthFlow,
  selectAuthDialogOpen,
  selectAuthFlow,
  selectAuthFlowCallbackUrl,
  selectAuthFlowComponent,
  selectAuthFlowEmail,
  selectAuthFlowEmailLocked,
  selectAuthFlowEmailVerify,
  selectAuthFlowStep,
  selectOrgNewsletterOptIn,
  setAuthFlow,
  setAuthFlowEmailVerify,
  setAuthFlowError,
  setAuthFlowForgottenPassword,
  setAuthFlowLoading,
  setAuthFlowPasswordErrors,
} from "./auth-slice"
import {
  mtCreateAsyncThunk,
  unwrapWithError,
} from "./enhanced-create-async-thunk"
import { AppDispatch, RootState } from "./redux-store"

const userApi = new UserApi(buildClientAxios())
const embeddedAuthApi = new EmbeddedAuthApi(buildClientAxios())
const authV2Api = new AuthV2Api()

let googleInitPromise: Promise<void> | null = null
let appleInitPromise: Promise<void> | null = null

interface ProviderSignInOutcome {
  registrationOccurred?: boolean
}

const REGISTER_GATE_TYPES_THAT_SKIP_TRAVEL_ASSISTANT_SETUP =
  new Set<RegisterGateTypes>([
    "accept-shared-trip",
    "view-collection",
    "join-organization",
    "join-creator-program",
    "signup-save-trip",
    "flight",
    "hotel",
    "book-package",
  ])

function getOAuthErrorMessage(error: unknown, state: RootState): string {
  const emailLocked = selectAuthFlowEmailLocked(state)
  const lockedEmail = selectAuthFlowEmail(state)
  if (
    emailLocked &&
    error instanceof ProviderMismatchException &&
    lockedEmail
  ) {
    return `The selected account is not valid for this invitation. Use your ${lockedEmail} email address.`
  }
  return getSignInStepErrorMessage(error)
}

export const initGoogleSignIn = mtCreateAsyncThunk<void, void>(
  "authSlice/initGoogleSignIn",
  async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch as AppDispatch
    if (googleInitPromise === null) {
      googleInitPromise = setupGoogleSignIn(
        (code) => {
          void dispatch(submitGoogleSignIn({ code }))
        },
        (e) => {
          console.error("Google sign in error:", e)
          dispatch(setAuthFlowLoading(false))
          dispatch(setAuthFlowError(getSignInStepErrorMessage(e)))
        }
      )
    }

    try {
      await googleInitPromise
    } catch (e) {
      googleInitPromise = null
      console.error("google sign in setup error", e)
      throw e
    }
  }
)

export const initAppleSignIn = mtCreateAsyncThunk<void, void>(
  "authSlice/initAppleSignIn",
  async (_) => {
    try {
      if (appleInitPromise === null) {
        appleInitPromise = setupAppleSignIn()
      }

      await appleInitPromise
    } catch (e) {
      console.error("apple sign in setup error", e)
      // Apple sign in initialization errors are silent - component handles display
    }
  }
)

export const googleSignIn = mtCreateAsyncThunk<
  ProviderSignInOutcome,
  GoogleSignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/googleSignIn", async (params, { getState, dispatch }) => {
  const state = getState()
  const callbackUrl = selectAuthFlowCallbackUrl(state)
  const setupAfterSignup = selectAuthFlow(state).setupTravelAssistantAfterSignup
  let registrationOccurred: boolean | undefined

  if (isEmbedded()) {
    const newsletterOptIn =
      selectOrgHasNewsletter(state) && selectOrgNewsletterOptIn(state)

    const res = await embeddedAuthApi.googleProviderSignIn(
      params.code,
      newsletterOptIn
    )
    storeEmbeddedTokens(res)
  } else {
    const authResult = await authV2Api.googleProviderAuth(params)
    registrationOccurred = authResult.registrationOccurred
  }

  const postAuthCallbackUrl =
    registrationOccurred && setupAfterSignup
      ? prepareTravelAssistantSetupAfterSignup(callbackUrl)
      : callbackUrl

  sendAnalyticsEvent({
    name:
      (registrationOccurred ?? selectAuthFlowStep(state) !== "signin")
        ? "RegisterUser"
        : "SignIn",
    properties: { method: "google" },
  })
  broadcastAuthEvent({
    type: registrationOccurred ? "auth-register" : "auth-signin",
  })
  await dispatch(handleAuthCallback({ callbackUrl: postAuthCallbackUrl }))
  return { registrationOccurred }
})

const submitGoogleSignIn = mtCreateAsyncThunk<
  void,
  GoogleSignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/submitGoogleSignIn", async (params, { dispatch, getState }) => {
  dispatch(setAuthFlowLoading(true))
  const step = selectAuthFlowStep(getState())

  try {
    await unwrapWithError(dispatch(googleSignIn(params)))
  } catch (error) {
    console.error("Google sign in error:", error)
    dispatch(setAuthFlowLoading(false))
    dispatch(setAuthFlowError(getOAuthErrorMessage(error, getState())))
    if (step === "signin") {
      sendAnalyticsEvent({
        name: "SignInFail",
        properties: { error: `${error}` },
      })
    } else {
      sendAnalyticsEvent({
        name: "RegisterUserFail",
        properties: { error: `${error}` },
      })
    }
  }
})

export const appleSignIn = mtCreateAsyncThunk<
  ProviderSignInOutcome,
  AppleSignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/appleSignIn", async (params, { getState, dispatch }) => {
  const state = getState()
  const callbackUrl = selectAuthFlowCallbackUrl(state)
  const setupAfterSignup = selectAuthFlow(state).setupTravelAssistantAfterSignup
  let registrationOccurred: boolean | undefined

  if (isEmbedded()) {
    const newsletterOptIn =
      selectOrgHasNewsletter(state) && selectOrgNewsletterOptIn(state)

    const appleParams = {
      ...params,
      organization_email_opt_in: newsletterOptIn,
    }

    const res = await embeddedAuthApi.appleProviderSignIn(appleParams)
    storeEmbeddedTokens(res)
  } else {
    const authResult = await authV2Api.appleProviderAuth(params)
    registrationOccurred = authResult.registrationOccurred
  }

  const postAuthCallbackUrl =
    registrationOccurred && setupAfterSignup
      ? prepareTravelAssistantSetupAfterSignup(callbackUrl)
      : callbackUrl

  sendAnalyticsEvent({
    name:
      (registrationOccurred ?? selectAuthFlowStep(state) !== "signin")
        ? "RegisterUser"
        : "SignIn",
    properties: { method: "apple" },
  })
  broadcastAuthEvent({
    type: registrationOccurred ? "auth-register" : "auth-signin",
  })
  await dispatch(handleAuthCallback({ callbackUrl: postAuthCallbackUrl }))
  return { registrationOccurred }
})

export const submitAppleSignIn = mtCreateAsyncThunk<
  void,
  AppleSignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/submitAppleSignIn", async (params, { dispatch, getState }) => {
  dispatch(setAuthFlowLoading(true))
  const step = selectAuthFlowStep(getState())

  try {
    await unwrapWithError(dispatch(appleSignIn(params)))
  } catch (error) {
    console.error("Apple sign in error:", error)
    dispatch(setAuthFlowLoading(false))
    dispatch(setAuthFlowError(getOAuthErrorMessage(error, getState())))
    if (step === "signin") {
      sendAnalyticsEvent({
        name: "SignInFail",
        properties: { error: `${error}` },
      })
    } else {
      sendAnalyticsEvent({
        name: "RegisterUserFail",
        properties: { error: `${error}` },
      })
    }
  }
})

export const webHandoff = mtCreateAsyncThunk<
  void,
  WebHandoffParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/webHandoff", async (params, { dispatch }) => {
  await authV2Api.webHandoff({ code: params.code })

  broadcastAuthEvent({ type: "auth-signin" })
  await dispatch(handleAuthCallback({ callbackUrl: params.callbackUrl }))
})

export const finishPhoneSignIn = mtCreateAsyncThunk<
  void,
  { callbackUrl?: string; registrationOccurred?: boolean },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/finishPhoneSignIn",
  async ({ callbackUrl, registrationOccurred }, { dispatch, getState }) => {
    if (registrationOccurred) {
      if (selectAuthFlow(getState()).setupTravelAssistantAfterSignup) {
        callbackUrl = prepareTravelAssistantSetupAfterSignup(callbackUrl)
      }
      broadcastAuthEvent({ type: "auth-register" })
    } else {
      broadcastAuthEvent({ type: "auth-signin" })
    }
    await dispatch(handleAuthCallback({ callbackUrl }))
  }
)

export const credentialSignIn = mtCreateAsyncThunk<
  void,
  SignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/credentialSignIn", async (params, { getState, dispatch }) => {
  const state = getState()

  if (isEmbedded()) {
    const newsletterOptIn =
      selectOrgHasNewsletter(state) && selectOrgNewsletterOptIn(state)

    const res = await embeddedAuthApi.signIn(
      params.email,
      params.password,
      newsletterOptIn
    )
    storeEmbeddedTokens(res)
  } else {
    await authV2Api.signIn(params)
  }

  sendAnalyticsEvent({ name: "SignIn", properties: { method: "email" } })
  broadcastAuthEvent({ type: "auth-signin" })

  await dispatch(handleAuthCallback({ callbackUrl: params.callbackUrl }))
})

export const submitCredentialSignIn = mtCreateAsyncThunk<
  void,
  SignInParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/submitCredentialSignIn", async (params, { dispatch }) => {
  dispatch(setAuthFlowLoading(true))

  try {
    await unwrapWithError(dispatch(credentialSignIn(params)))
  } catch (error) {
    console.error("Credential sign in error:", error)
    dispatch(setAuthFlowLoading(false))

    sendAnalyticsEvent({
      name: "SignInFail",
      properties: { error: `${error}` },
    })
    dispatch(setAuthFlowError(getSignInStepErrorMessage(error)))
  }
})

export const credentialRegister = mtCreateAsyncThunk<
  void,
  RegisterParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/credentialRegister", async (params, { getState, dispatch }) => {
  const state = getState()
  const setupAfterSignup = selectAuthFlow(state).setupTravelAssistantAfterSignup
  const embedded = isEmbedded()

  if (embedded) {
    const newsletterOptIn =
      params.organization_email_opt_in ??
      (selectOrgHasNewsletter(state) && selectOrgNewsletterOptIn(state))

    const apiParams: EmbeddedRegisterApiParams = {
      email: params.email,
      password: params.password,
      first_name: params.first_name,
      last_name: params.last_name,
      organization_email_opt_in: newsletterOptIn,
      cf_token: params.cf_token,
    }

    const res = await embeddedAuthApi.register(apiParams)
    storeEmbeddedTokens(res)
  } else {
    await authV2Api.register(params)
  }

  const callbackUrl =
    !embedded && setupAfterSignup
      ? prepareTravelAssistantSetupAfterSignup(params.callbackUrl)
      : params.callbackUrl

  sendAnalyticsEvent({ name: "RegisterUser", properties: { method: "email" } })
  broadcastAuthEvent({ type: "auth-register" })

  await dispatch(handleAuthCallback({ callbackUrl }))
})

export const submitCredentialRegister = mtCreateAsyncThunk<
  void,
  RegisterParams,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/submitCredentialRegister", async (params, { dispatch }) => {
  dispatch(setAuthFlowLoading(true))

  try {
    await unwrapWithError(dispatch(credentialRegister(params)))
  } catch (error) {
    console.error("Credential register error:", error)
    sendAnalyticsEvent({
      name: "RegisterUserFail",
      properties: { error: `${error}` },
    })
    dispatch(setAuthFlowLoading(false))
    dispatch(setAuthFlowError(getRegisterStepErrorMessage(error)))
  }
})

export const openAuthDialog = mtCreateAsyncThunk<
  void,
  {
    initAuthComponent?: AuthComponent
    callbackUrl?: string
    customTitle?: string
    customLeadText?: string
    preventClose?: boolean
    setupTravelAssistantAfterSignup?: boolean
    email?: string
    emailLocked?: boolean
    cancelCallbackUrl?: string
  },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/openAuthDialog",
  async (
    {
      initAuthComponent,
      callbackUrl,
      preventClose,
      customLeadText,
      setupTravelAssistantAfterSignup,
      customTitle,
      email,
      emailLocked: emailLockedParam,
      cancelCallbackUrl,
    },
    { dispatch, getState }
  ) => {
    const baseAuthFlow = {
      ...initialAuthFlow(),
      setupTravelAssistantAfterSignup: setupTravelAssistantAfterSignup ?? true,
    }
    const prevOpen = selectAuthDialogOpen(getState())
    const component = initAuthComponent ?? "auth-form"

    if (component === "verify_email") {
      await userApi.generateVerifyEmail("check")
    }

    if (!prevOpen) {
      // set closed before full reset to avoid flashing text changes
      dispatch(_resetAuthDialogProps())
      dispatch(setAuthFlow(initialAuthFlow()))

      // Send amplitude event
      void dispatch(
        sendAuthComponentAmplitudeEvent({ component, opened: true })
      )
    }

    const emailLocked = !!(email?.trim() && emailLockedParam)

    // If an email is provided and we're on the auth-form component,
    // check providers and navigate to the appropriate step
    if (email?.trim() && component === "auth-form") {
      try {
        const { value: providersLinkedToEmail } =
          await userApi.getProvidersByEmail(email.trim())

        if (!providersLinkedToEmail.length) {
          // Set up register step with email
          dispatch(
            setAuthFlow({
              ...baseAuthFlow,
              component: component,
              step: "register",
              customTitle,
              customLeadText,
              callbackUrl: callbackUrl ?? "/",
              email: email.trim(),
              providers: ["google", "apple", "email"],
              emailLocked,
            })
          )
        } else {
          // Set up sign-in step with email and providers
          dispatch(
            setAuthFlow({
              ...baseAuthFlow,
              component: component,
              step: "signin",
              customTitle,
              customLeadText,
              callbackUrl: callbackUrl ?? "/",
              email: email.trim(),
              providers: providersLinkedToEmail,
              emailLocked,
            })
          )
        }
      } catch (error) {
        console.error("Email validation error in openAuthDialog:", error)
        // On error, stay on email step but with the email pre-filled
        dispatch(
          setAuthFlow({
            ...baseAuthFlow,
            component: component,
            customTitle,
            customLeadText,
            callbackUrl: callbackUrl ?? "/",
            email: email?.trim() ?? "",
            error: getEmailStepErrorMessage(error),
            emailLocked,
          })
        )
      }
    } else {
      // No email provided or not auth-form component, use normal flow
      dispatch(
        setAuthFlow({
          ...baseAuthFlow,
          component: component,
          customTitle,
          customLeadText,
          callbackUrl: callbackUrl ?? "/",
          email: email?.trim() ?? "",
          emailLocked: false,
        })
      )
    }

    dispatch(
      _setAuthDialogProps({
        open: true,
        preventClose: !!preventClose,
        cancelCallbackUrl,
      })
    )
  }
)

export const closeAuthDialog = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/closeAuthDialog", async (_, { dispatch, getState }) => {
  const state = getState()
  const prevOpen = selectAuthDialogOpen(state)
  const component = selectAuthFlowComponent(state)

  if (prevOpen && getPendingAuthAction()?.requiresAuthentication) {
    clearPendingAuthAction()
  }

  dispatch(
    _setAuthDialogProps({
      open: false,
      preventClose: false,
      cancelCallbackUrl: undefined,
    })
  )

  if (!prevOpen) {
    // If the dialog was not open, we don't need to do anything
    return
  }

  // Close the dialog
  void dispatch(sendAuthComponentAmplitudeEvent({ component, opened: false }))
})

export const showRegisterGateDialog = mtCreateAsyncThunk<
  void,
  RegisterGateDialogInfo | RegisterGateTypes,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/showRegisterGateDialog", async (param, { dispatch, getState }) => {
  const dialogInfo = typeof param === "string" ? { type: param } : param
  const state = getState()
  const currentUser = selectUser(state)

  // Only send the "DialogRegistrationGateOpen" for when user is not yet authenticated
  // and we are bringing up sign in dialog
  // For the verify email, we have another event: DialogVerifyEmailOpen
  if (!currentUser.provider || currentUser.provider === "anonymous") {
    setAnalyticsUserProperties({
      type: "registerGateProps",
      where: dialogInfo.type,
    })
    sendAnalyticsEvent({
      name: "DialogRegistrationGateOpen",
      properties: {
        dialog: {
          title: "Register Gate",
          subTitle: dialogInfo.type,
        },
      },
    })
  }

  const email =
    dialogInfo && "email" in dialogInfo ? dialogInfo.email : undefined
  const emailLocked =
    dialogInfo && "emailLocked" in dialogInfo ? dialogInfo.emailLocked : false

  let callbackUrl: string
  const constructUrlWithParams = (url: string, params: string | undefined) => {
    return present(params) ? `${url}?${params}` : url
  }

  const queryParamsStr = dialogInfo.queryParams
  switch (dialogInfo.type) {
    case "setup-travel-assistant": {
      const pendingSetup = getPendingAuthAction()
      callbackUrl = pendingSetup?.returnToChatId
        ? links.chat(pendingSetup.returnToChatId)
        : pendingSetup?.source === "chat"
          ? removeParamFromUrl(
              `${mtLocation.pathname}${mtLocation.search}`,
              "start"
            )
          : constructUrlWithParams("/chat/new", queryParamsStr)
      break
    }
    case "join-creator-program":
      callbackUrl = constructUrlWithParams(
        "/join-creator-program",
        queryParamsStr
      )
      break
    default:
      callbackUrl = mtLocation.pathname + mtLocation.search
  }

  const cancelCallbackUrl =
    dialogInfo?.type === "accept-shared-trip" ? appLandingPath() : undefined

  const preventCloseTypes = [
    "accept-shared-trip",
    "view-collection",
    "place-detailed",
  ]
  const preventClose = dialogInfo?.type
    ? preventCloseTypes.includes(dialogInfo.type)
    : false
  const setupTravelAssistantAfterSignup =
    dialogInfo.setupTravelAssistantAfterSignup ??
    !REGISTER_GATE_TYPES_THAT_SKIP_TRAVEL_ASSISTANT_SETUP.has(dialogInfo.type)

  let initAuthComponent: AuthComponent = "auth-form"
  if (currentUser.provider === "email" && !currentUser.email_validated) {
    initAuthComponent = "verify_email"
  }

  // Only send the "DialogRegistrationGateOpen" for when user is not yet authenticated
  // and we are bringing up sign in dialog
  // For the verify email, we have another event: DialogVerifyEmailOpen
  if (!currentUser.email_validated && dialogInfo) {
    setAnalyticsUserProperties({
      type: "registerGateProps",
      where: dialogInfo.type,
    })
  }

  let customTitle: string
  let customLeadText: string | undefined = undefined

  switch (dialogInfo.type) {
    case "message":
    case "message-per-trip":
      customTitle = "Keep the conversation going"
      break
    case "customize-trip":
      customTitle = "Customize your trip"
      break
    case "save-progress":
      customTitle = "Save your progress"
      break
    case "saved":
      customTitle = "Curate your faves"
      break
    case "invite-collaborators":
      customTitle = "Invite your crew"
      break
    case "accept-shared-trip":
      customTitle = "Welcome to the trip!"
      break
    case "view-collection":
      customTitle = "View this collection"
      break
    case "signup-save-trip":
      customTitle = "Save your itinerary"
      break
    case "add-itinerary-comment":
      customTitle = "Save your comments"
      break
    case "explore":
    case "place-detailed":
      customTitle = "Keep exploring"
      break
    case "create-vanity-link":
      customTitle = "Create a guide"
      break
    case "share-chat":
      customTitle = "Share your chat"
      break
    case "share-trip":
      customTitle = "Share your trip"
      break
    case "add-review":
      customTitle = "Add your review"
      break
    case "listen":
      customTitle = "Keep listening"
      break
    case "setup-travel-assistant":
      customTitle = "Set up your travel assistant"
      customLeadText =
        "Create a Mindtrip account to personalize your assistant."
      break
    case "flight":
      customTitle = "Sign up to book this flight"
      customLeadText =
        "Create a Mindtrip account to access your booked flights."
      break
    default:
      customTitle = "Log in or create an account"
  }

  if (customTitle && !customLeadText) {
    customLeadText = "Just enter your email to sign up or log in to Mindtrip."
  }

  await dispatch(
    openAuthDialog({
      initAuthComponent,
      callbackUrl,
      customTitle,
      customLeadText,
      preventClose,
      email,
      emailLocked,
      cancelCallbackUrl,
      setupTravelAssistantAfterSignup,
    })
  )
})

function trackEmailAuthStep(
  step: "email" | "signin" | "register",
  outcome?: "success" | "failure",
  errorType?: "validation" | "request_failed",
  nextStep?: "signin" | "register"
) {
  sendAnalyticsEvent({
    name: outcome ? "AuthStepResult" : "AuthStepSubmit",
    properties: {
      method: "email",
      step,
      ...(outcome && { outcome }),
      ...(errorType && { error_type: errorType }),
      ...(nextStep && { next_step: nextStep }),
    },
  })
}

export const handleEmailStepSubmit = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/handleAuthFlowSubmit", async (_, { dispatch, getState }) => {
  const state = getState()
  const currVal = selectAuthFlow(state)
  trackEmailAuthStep("email")
  const email = currVal.email.trim()
  if (!email) {
    trackEmailAuthStep("email", "failure", "validation")
    if (currVal.providers.length > 0) {
      dispatch(
        setAuthFlowError("Enter an email or use one of the options below")
      )
      return
    }

    dispatch(setAuthFlowError("Enter an email"))
    return
  }

  if (!EmailSchema.safeParse({ email }).success) {
    trackEmailAuthStep("email", "failure", "validation")
    dispatch(setAuthFlowError("Enter a valid email"))
    return
  }

  dispatch(setAuthFlowLoading(true))

  try {
    const { value: providersLinkedToEmail } =
      await userApi.getProvidersByEmail(email)
    trackEmailAuthStep(
      "email",
      "success",
      undefined,
      providersLinkedToEmail.length ? "signin" : "register"
    )
    if (!providersLinkedToEmail.length) {
      void dispatch(showRegisterStep({ email }))
    } else {
      void dispatch(
        showSignInStep({ email, providers: providersLinkedToEmail })
      )
    }
  } catch (error) {
    trackEmailAuthStep("email", "failure", "request_failed")
    console.error("Email Step Error:", error)
    dispatch(setAuthFlowError(getEmailStepErrorMessage(error)))
    dispatch(setAuthFlowLoading(false))
  }
})

export const handleSignInStepSubmit = mtCreateAsyncThunk<
  void,
  {
    formValid: boolean
  },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/handleSignInStepSubmit",
  async ({ formValid }, { dispatch, getState }) => {
    const state = getState()
    const currVal = selectAuthFlow(state)
    trackEmailAuthStep("signin")

    const valid =
      formValid && (!shouldTurnstile("sign-in") || currVal.cfToken.length > 1)
    if (!valid) {
      trackEmailAuthStep("signin", "failure", "validation")
      dispatch(
        setAuthFlowError(
          !formValid ? "Fill out both fields" : "Unable to sign in"
        )
      )
      return
    }

    await dispatch(
      submitCredentialSignIn({
        email: currVal.email,
        password: currVal.password,
        callbackUrl: currVal.callbackUrl,
        cf_token: currVal.cfToken,
      })
    )
  }
)

export const showEmailStep = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/showEmailStep", async (_, { dispatch, getState }) => {
  const state = getState()
  const currVal = selectAuthFlow(state)

  dispatch(
    setAuthFlow({
      ...currVal,
      component: "auth-form",
      step: "email",
      email: "",
      providers: [],
      loading: false,
      error: undefined,
      passwordErrors: [],
      passwordTouched: false,
      // Clear emailLocked when going back to email step
      emailLocked: false,
    })
  )
})

export const showSignInStep = mtCreateAsyncThunk<
  void,
  { email: string; providers: LoginAuthProvider[] },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/showSignInStep",
  async ({ email, providers }, { dispatch, getState }) => {
    const state = getState()
    const currAuthFlow = selectAuthFlow(state)

    dispatch(
      setAuthFlow({
        ...currAuthFlow,
        component: "auth-form",
        step: "signin",
        email: email.trim(),
        password: "",
        providers,
        loading: false,
        error: undefined,
        passwordErrors: [],
        passwordTouched: false,
        // Preserve emailLocked state
        emailLocked: currAuthFlow.emailLocked,
      })
    )
  }
)

export const showRegisterStep = mtCreateAsyncThunk<
  void,
  { email: string },
  { state: RootState; dispatch: AppDispatch }
>("authSlice/showRegisterStep", async ({ email }, { dispatch, getState }) => {
  const state = getState()
  const currAuthFlow = selectAuthFlow(state)

  dispatch(
    setAuthFlow({
      ...currAuthFlow,
      component: "auth-form",
      step: "register",
      email: email.trim(),
      password: "",
      providers: ["google", "apple", "email"],
      loading: false,
      error: undefined,
      passwordErrors: [],
      passwordTouched: false,
      // Preserve emailLocked state
      emailLocked: currAuthFlow.emailLocked,
    })
  )
})

export const showForgottenPasswordStep = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/showForgottenPasswordStep", async (_, { dispatch, getState }) => {
  const state = getState()
  const currAuthFlow = selectAuthFlow(state)

  dispatch(
    setAuthFlow({
      ...currAuthFlow,
      component: "forgotten",
      error: undefined,
      step: "email",
      forgottenPassword: {
        step: "form",
        submitting: false,
      },
    })
  )

  // The sign-in page shows the flow inline, so only the dialog, when it is the
  // one showing the flow, needs to stay open and become closable.
  if (selectAuthDialogOpen(state)) {
    dispatch(
      _setAuthDialogProps({
        open: true,
        preventClose: false,
      })
    )
  }
})

export const handleForgotPasswordGoToSignIn = mtCreateAsyncThunk<
  void,
  { email: string },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/handleForgotPasswordGoToSignIn",
  async ({ email }, { dispatch }) => {
    dispatch(setAuthFlowLoading(true))

    try {
      const { value: providersLinkedToEmail } =
        await userApi.getProvidersByEmail(email)

      if (!providersLinkedToEmail.length) {
        void dispatch(showRegisterStep({ email }))
      } else {
        void dispatch(
          showSignInStep({ email, providers: providersLinkedToEmail })
        )
      }
    } catch (error) {
      console.error(error)
      dispatch(setAuthFlowError(getRegisterStepErrorMessage(error)))
      dispatch(setAuthFlowLoading(false))
    }
  }
)

export const handleForgotPasswordSubmit = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/handleForgotPasswordSubmit", async (_, { dispatch, getState }) => {
  const state = getState()
  const email = selectAuthFlowEmail(state)
  const callbackUrl = selectAuthFlowCallbackUrl(state)
  const forgottenPassword = selectAuthFlow(state).forgottenPassword

  dispatch(
    setAuthFlowForgottenPassword({
      ...forgottenPassword,
      submitting: true,
    })
  )
  dispatch(setAuthFlowError(undefined))

  const authorizedClientAxios = buildClientAxios()

  try {
    await authorizedClientAxios.post<void>("/api/auth/forgotten_password", {
      email,
      callbackUrl,
    })
    dispatch(
      setAuthFlowForgottenPassword({
        step: "success",
        submitting: false,
      })
    )
    sendAnalyticsEvent("ForgotPasswordSubmit")
  } catch (error) {
    if (error instanceof ProviderMismatchException) {
      dispatch(
        setAuthFlowForgottenPassword({
          step: "mismatch",
          submitting: false,
        })
      )
      sendAnalyticsEvent({
        name: "ForgotPasswordFail",
        properties: errorToAmplitudeProperties("Provider mismatch"),
      })
      return
    }

    const errorBody =
      error instanceof SimpleHttpError
        ? (error.response?.data as ServerErrorBody | undefined)
        : undefined

    sendAnalyticsEvent({
      name: "ForgotPasswordFail",
      properties: errorToAmplitudeProperties(
        errorBody ? errorBody.error : "Forgotten password failed"
      ),
    })

    dispatch(
      setAuthFlowError(
        errorBody?.error ||
          "An error occurred, could not reset password at this time"
      )
    )
    dispatch(
      setAuthFlowForgottenPassword({
        ...forgottenPassword,
        submitting: false,
      })
    )
  }
})

export const showEmailVerifyStep = mtCreateAsyncThunk<
  void,
  { displayEmail?: string } | void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/showEmailVerifyStep", async (params, { dispatch, getState }) => {
  const state = getState()
  const currAuthFlow = selectAuthFlow(state)

  // Initialize verify email generation
  const userApi = new UserApi(buildClientAxios())
  await userApi.generateVerifyEmail("check")
  sendAnalyticsEvent({
    name: "DialogVerifyEmailOpen",
    properties: {
      dialog: {
        title: "Verify Email",
        subTitle: AmplitudeEmptyValue,
      },
    },
  })

  dispatch(
    setAuthFlow({
      ...currAuthFlow,
      component: "verify_email",
      emailVerify: {
        step: "form",
        verifying: false,
        resending: false,
        message: undefined,
        error: undefined,
        displayEmail: params?.displayEmail,
      },
    })
  )

  dispatch(
    _setAuthDialogProps({
      open: true,
      preventClose: false,
    })
  )
})

export const handleEmailVerifyResend = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/handleEmailVerifyResend", async (_, { dispatch, getState }) => {
  const state = getState()
  const emailVerify = selectAuthFlowEmailVerify(state)
  const currentUser = selectUser(state)

  dispatch(
    setAuthFlowEmailVerify({
      ...emailVerify,
      resending: true,
      message: undefined,
      error: undefined,
    })
  )

  try {
    const userApi = new UserApi(buildClientAxios())
    const ret = await userApi.generateVerifyEmail()

    if (ret.status === "ok") {
      dispatch(
        setAuthFlowEmailVerify({
          ...emailVerify,
          resending: false,
          message: "Email has been sent. Please check your inbox.",
        })
      )
      sendAnalyticsEvent({
        name: "EmailVerifyResend",
        properties: {
          handle: currentUser.handle,
        },
      })
    } else {
      throw new Error(ret.message)
    }
  } catch (error) {
    console.error(error)
    Honeybadger.notify(
      `Verify Email Dialog: Error in generating verification email for user: ${currentUser.email}`
    )
    dispatch(
      setAuthFlowEmailVerify({
        ...emailVerify,
        resending: false,
        error: "Error sending verification email. Please try again later.",
      })
    )
  }
})

export const handleEmailVerifySubmit = mtCreateAsyncThunk<
  void,
  { code: string; router: NextRouter },
  { state: RootState; dispatch: AppDispatch }
>(
  "authSlice/handleEmailVerifySubmit",
  async ({ code, router }, { dispatch, getState }) => {
    const state = getState()
    const emailVerify = selectAuthFlowEmailVerify(state)
    const currentUser = selectUser(state)

    if (code.length !== 6) {
      dispatch(
        setAuthFlowEmailVerify({
          ...emailVerify,
          error: "Verification code should be 6 characters long.",
        })
      )
      return
    }

    dispatch(
      setAuthFlowEmailVerify({
        ...emailVerify,
        verifying: true,
        error: undefined,
      })
    )

    try {
      const userApi = new UserApi(buildClientAxios())
      const success = await userApi.verifyEmail(code)

      if (success.status === "ok" || success.status === "redeemed") {
        await refreshAuthToken()
        if (success.email_changed) {
          router.reload()
        } else {
          await dispatch(refreshCurrentUserInfo())
        }
        dispatch(
          setAuthFlowEmailVerify({
            step: "verified",
            verifying: false,
            resending: false,
            message: undefined,
            error: undefined,
          })
        )
      } else if (success.status === "expired") {
        dispatch(
          setAuthFlowEmailVerify({
            ...emailVerify,
            verifying: false,
            error:
              "Expired verification code. Make sure to use the most recent code, or resend a new code and try again.",
            message: undefined,
          })
        )
      } else {
        dispatch(
          setAuthFlowEmailVerify({
            ...emailVerify,
            verifying: false,
            error:
              "Incorrect verification code. Make sure you typed the code correctly and try again.",
          })
        )
      }
    } catch (error) {
      console.error(error)
      Honeybadger.notify(
        `Verify Email Dialog: Error submitting verification code for user: ${currentUser.email}`
      )
      dispatch(
        setAuthFlowEmailVerify({
          ...emailVerify,
          verifying: false,
          error: "An error occurred. Please try again.",
        })
      )
    }
  }
)

export const handleRegisterStepSubmit = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("authSlice/handleRegisterStepSubmit", async (_, { dispatch, getState }) => {
  const state = getState()
  const currVal = selectAuthFlow(state)
  trackEmailAuthStep("register")
  const hasNewsletter = selectOrgHasNewsletter(state)
  const newsletterOptIn = selectOrgNewsletterOptIn(state)

  const [isValid, validated, errors] = validateRegistrationFormValue({
    email: currVal.email,
    password: currVal.password,
    cf_token: currVal.cfToken,
    cf_token_required: shouldTurnstile("register"),
    organization_email_opt_in: hasNewsletter ? newsletterOptIn : false,
  })

  if (!isValid || !validated) {
    trackEmailAuthStep("register", "failure", "validation")
    if (errors?.password?._errors) {
      dispatch(setAuthFlowPasswordErrors(errors.password._errors))
    }

    if (errors?.email?._errors?.length) {
      dispatch(setAuthFlowError(errors.email._errors[0]))
    } else if (errors?.password?._errors?.length) {
      dispatch(setAuthFlowError(errors.password._errors[0]))
    } else if (errors?.cf_token?._errors?.length) {
      dispatch(setAuthFlowError("Please complete the verification"))
    } else {
      dispatch(setAuthFlowError("Please fill out all required fields"))
    }
    return
  }

  dispatch(setAuthFlowLoading(true))

  await dispatch(
    submitCredentialRegister({
      email: currVal.email,
      password: currVal.password,
      callbackUrl: currVal.callbackUrl,
      cf_token: currVal.cfToken,
      organization_email_opt_in: hasNewsletter ? newsletterOptIn : undefined,
    })
  )
})

export const handleAuthCallback = mtCreateAsyncThunk<
  void,
  { callbackUrl?: string },
  { state: RootState; dispatch: AppDispatch }
>("authSlice/handleAuthCallback", async ({ callbackUrl }) => {
  if (isEmbedded()) {
    mtLocation.reload()
  } else {
    if (callbackUrl && isSafeRelativeCallbackUrl(callbackUrl)) {
      if (mtLocation.pathname === callbackUrl) {
        mtLocation.reload()
      } else {
        mtLocation.replace(callbackUrl)
      }
    }
  }
})

export const sendAuthComponentAmplitudeEvent = mtCreateAsyncThunk<
  void,
  { component: string; opened: boolean },
  {}
>(
  "authSlice/sendAuthComponentAmplitudeEvent",
  async ({ component, opened }) => {
    switch (component) {
      case "auth-form":
        sendAnalyticsEvent({
          name: opened ? "DialogAuthOpen" : "DialogAuthClose",
          properties: {
            dialog: {
              title: "Login Dialog",
              subTitle: AmplitudeEmptyValue,
            },
          },
        })
        break
      case "forgotten":
        sendAnalyticsEvent({
          name: opened
            ? "DialogForgotPasswordOpen"
            : "DialogForgotPasswordClose",
          properties: {
            dialog: {
              title: "Forgot Password",
              subTitle: AmplitudeEmptyValue,
            },
          },
        })
        break
      case "verify_email":
        sendAnalyticsEvent({
          name: opened ? "DialogVerifyEmailOpen" : "DialogVerifyEmailClose",
          properties: {
            dialog: {
              title: "Verify Email",
              subTitle: AmplitudeEmptyValue,
            },
          },
        })
        break
      case "reset_password":
        // no-op
        break
    }
  }
)
